import {type CreateClientRequestStore, CreateClientSteps, DefaultAliiOCRType} from '$lib/models/client/types';
import {MenuConfig} from '$lib/models/types';
import {derived, writable} from 'svelte/store';

function createCreateClientStore() {
  const request = writable<CreateClientRequestStore>(getInitialValue());

  const isLoading = writable(true);
  const currentStep = writable(CreateClientSteps.ClientDetails);

  function isClientDetailsStepValid(request: CreateClientRequestStore) {
    return (
      request.clientName !== undefined &&
      request.clientName.trim().length > 0 &&
      request.clientStatus !== undefined &&
      request.connectionTypeId !== undefined &&
      request.exportTypeId !== undefined &&
      request.domains.length > 0
    );
  }

  function isInitialUserStepValid(request: CreateClientRequestStore) {
    return (
      request.initialUserDetails.username !== undefined &&
      request.initialUserDetails.username.trim().length > 0 &&
      request.initialUserDetails.password !== undefined &&
      request.initialUserDetails.password.trim().length > 0 &&
      request.initialUserDetails.name !== undefined &&
      request.initialUserDetails.name.trim().length > 0
    );
  }

  function getInitialValue(): CreateClientRequestStore {
    return {
      clientName: undefined,
      clientStatus: undefined,
      connectionTypeId: undefined,
      exportTypeId: undefined,
      initialUserDetails: {
        username: undefined,
        password: undefined,
        name: undefined,
      },
      domains: [],
      menuConfigs: Object.values(MenuConfig).map((config) => ({ name: config, enabled: false })),
      createNewOrganisation: true,
      organisationId: undefined,
      organisationName: undefined,
      location: 'AU',
      aliiOCRType: DefaultAliiOCRType,
    };
  }

  return {
    isLoading,
    request,
    currentStep,
    maxValidStep: derived([request], ([$request]) => {
      if (!isClientDetailsStepValid($request)) {
        return CreateClientSteps.ClientDetails;
      }

      if (!isInitialUserStepValid($request)) {
        return CreateClientSteps.InitialUser;
      }

      return CreateClientSteps.Review;
    }),
    currentStepValid: derived([request, currentStep], ([$request, $currentStep]) => {
      if ($currentStep === CreateClientSteps.ClientDetails) {
        return isClientDetailsStepValid($request);
      }

      if ($currentStep === CreateClientSteps.InitialUser) {
        return isInitialUserStepValid($request);
      }

      return true;
    }),
    setClientStatus: (clientStatus: number | undefined) => {
      request.update((client) => ({ ...client, clientStatus }));
    },
    setExportTypeId: (exportTypeId: number | undefined) => {
      request.update((client) => ({ ...client, exportTypeId }));
    },
    setConnectionTypeId: (connectionTypeId: number | undefined) => {
      request.update((client) => ({ ...client, connectionTypeId }));
    },
    addDomain: (domain: string) => {
      request.update((client) => ({ ...client, domains: [...client.domains, domain] }));
    },
    removeDomain(index: number) {
      request.update((client) => ({
        ...client,
        domains: client.domains.filter((_, i) => i !== index),
      }));
    },
    setOrganisationMode: (createNew: boolean) => {
      request.update((client) => ({
        ...client,
        createNewOrganisation: createNew,
        organisationId: undefined,
        organisationName: undefined,
      }));
    },
    setOrganisationId: (id: string | undefined, displayName?: string) => {
      request.update((client) => ({
        ...client,
        organisationId: id,
        organisationName: displayName ?? client.organisationName,
      }));
    },
    applyOrgDefault: () => {
      request.update((client) => {
        if (client.createNewOrganisation && !client.organisationName?.trim()) {
          return { ...client, organisationName: client.clientName };
        }
        return client;
      });
    },
    reset() {
      request.set(getInitialValue());
      currentStep.set(CreateClientSteps.ClientDetails);
      isLoading.set(true);
    },
  };
}

export const createClientStore = createCreateClientStore();
